import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { MessageSquare, X, Send, Sparkles } from "lucide-react";
import useSiteConfig from "@/hooks/useSiteConfig";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ChatWidget() {
  const { config } = useSiteConfig();
  const botName = config.brand.chatbotName || "QUASAR";
  const brandShort = config.brand.shortName || "Quantum Star";

  const initialMessage = {
    role: "assistant",
    content: `Hi, I'm ${botName} — ${brandShort}'s AI growth strategist. Tell me a little about your business and your #1 growth bottleneck, and I'll show you how we'd scale it.`,
  };

  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([initialMessage]);
  const [input, setInput] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [warming, setWarming] = useState(false);
  const [showNudge, setShowNudge] = useState(false);
  const warmedRef = useRef(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading, open]);

  // Broadcast open/close so other floating UI (e.g. WhatsApp button) can
  // hide itself and avoid stacking collisions on mobile.
  useEffect(() => {
    try {
      window.dispatchEvent(
        new CustomEvent("quasar:chat-state", { detail: { open } })
      );
    } catch {
      // CustomEvent should be universally supported; swallow defensively
    }
  }, [open]);

  // After 45s of dwell on the page, surface a small "unread"-style nudge
  // bubble next to the FAB. Strictly one-shot per session — never re-fires
  // on repeat visits, and disappears the moment the visitor opens the chat
  // or auto-times-out after 18s so it never overstays its welcome.
  useEffect(() => {
    if (open) return undefined;
    let dismissed = false;
    try {
      dismissed = sessionStorage.getItem("quasar_nudge_seen") === "1";
    } catch {
      /* incognito storage can throw — treat as not-dismissed */
    }
    if (dismissed) return undefined;

    const showT = setTimeout(() => {
      setShowNudge(true);
      try {
        sessionStorage.setItem("quasar_nudge_seen", "1");
      } catch {
        /* noop */
      }
    }, 45000);
    return () => clearTimeout(showT);
  }, [open]);

  // Auto-hide the nudge after 18s of being visible (don't pester).
  useEffect(() => {
    if (!showNudge) return undefined;
    const hideT = setTimeout(() => setShowNudge(false), 18000);
    return () => clearTimeout(hideT);
  }, [showNudge]);

  // Any time the chat opens, the nudge is immediately resolved.
  useEffect(() => {
    if (open) setShowNudge(false);
  }, [open]);

  // When the widget opens, send a lightweight warm-up ping so Render free-tier
  // backends spin up before the user's first real message. Idempotent: only
  // runs once per page load.
  useEffect(() => {
    if (!open || warmedRef.current) return;
    warmedRef.current = true;
    setWarming(true);
    axios
      .get(`${API}/`, { timeout: 45000 })
      .catch(() => {})
      .finally(() => setWarming(false));
  }, [open]);

  // Contextual auto-open: ServiceDetailsModal / DetailsModal fires a
  // `quasar:contextual-open` event after 25s of dwell time. We open the
  // widget with a primed contextual prompt — but only once per session so
  // we never feel intrusive on repeat visits or multiple modal views.
  useEffect(() => {
    const handler = (e) => {
      try {
        if (sessionStorage.getItem("quasar_contextual_fired") === "1") return;
        sessionStorage.setItem("quasar_contextual_fired", "1");
      } catch {
        // sessionStorage can throw in incognito on some browsers — just continue
      }
      const ctxName = e?.detail?.name || "this service";
      const kind = e?.detail?.kind || "service";
      const primed = buildContextualPrompt(ctxName, kind, botName);
      setMessages((m) => [...m, { role: "assistant", content: primed }]);
      setOpen(true);
    };
    window.addEventListener("quasar:contextual-open", handler);
    return () => window.removeEventListener("quasar:contextual-open", handler);
  }, [botName]);

  // Single retry with longer timeout — handles Render cold starts (~30-60s).
  const postChat = async (text) => {
    const payload = { message: text, session_id: sessionId };
    try {
      return await axios.post(`${API}/chat`, payload, { timeout: 75000 });
    } catch (err) {
      const code = err?.code;
      const status = err?.response?.status;
      const retriable =
        code === "ECONNABORTED" ||
        code === "ERR_NETWORK" ||
        status === 502 ||
        status === 503 ||
        status === 504;
      if (!retriable) throw err;
      // wait a beat, then retry once
      await new Promise((r) => setTimeout(r, 1500));
      return axios.post(`${API}/chat`, payload, { timeout: 75000 });
    }
  };

  const send = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }]);
    setLoading(true);
    try {
      const res = await postChat(text);
      setSessionId(res.data.session_id);
      setMessages((m) => [...m, { role: "assistant", content: res.data.reply }]);
    } catch (err) {
      console.error("chat error", err);
      const isTimeout = err?.code === "ECONNABORTED" || err?.code === "ERR_NETWORK";
      const fallback = isTimeout
        ? "Our AI servers are spinning up (Render free tier sleeps after 15min of inactivity). Please wait ~30 seconds and try again — or book a strategy call above for an instant response."
        : "Looks like our connection hit a snag. Please try again in a moment, or book a strategy call directly above.";
      setMessages((m) => [...m, { role: "assistant", content: fallback }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <AnimatePresence>
        {showNudge && !open && (
          <motion.button
            type="button"
            onClick={() => setOpen(true)}
            data-testid="chat-nudge-bubble"
            initial={{ opacity: 0, y: 8, scale: 0.94 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 6, scale: 0.94 }}
            transition={{ duration: 0.32, ease: "easeOut" }}
            aria-label={`Open chat with ${botName}`}
            className="fixed bottom-[9.25rem] right-6 sm:bottom-[5.25rem] z-[59] max-w-[260px] sm:max-w-[280px] text-left rounded-2xl glass-strong px-3.5 py-2.5 pr-9 shadow-[0_10px_40px_-10px_rgba(157,76,221,0.45)] hover:scale-[1.02] active:scale-[0.99] transition-transform"
          >
            <span className="absolute -top-1.5 left-3 inline-flex h-2.5 w-2.5 rounded-full bg-emerald-400 ring-2 ring-[var(--c-bg)] pulse-dot" />
            <div className="flex items-center gap-1.5 text-[10px] font-mono-acc uppercase tracking-[0.18em] text-emerald-400">
              <Sparkles size={10} className="text-cyan-300" />
              {botName} • 1 new
            </div>
            <div className="mt-1 text-[12.5px] leading-snug text-zinc-100">
              Want a free 90-day growth plan for your business?
            </div>
            <span
              onClick={(e) => {
                e.stopPropagation();
                setShowNudge(false);
              }}
              className="absolute top-1.5 right-1.5 inline-flex h-6 w-6 items-center justify-center rounded-full text-zinc-400 hover:text-white hover:bg-white/10 transition-colors"
              role="button"
              tabIndex={0}
              aria-label="Dismiss"
              data-testid="chat-nudge-dismiss"
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  e.stopPropagation();
                  setShowNudge(false);
                }
              }}
            >
              <X size={12} />
            </span>
          </motion.button>
        )}
      </AnimatePresence>

      <motion.button
        onClick={() => setOpen((v) => !v)}
        data-testid="chat-widget-toggle"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1, duration: 0.4 }}
        className="fixed bottom-[5.5rem] right-6 sm:bottom-6 z-[60] inline-flex items-center gap-2 rounded-full bg-white text-black px-4 sm:px-5 py-3.5 text-sm font-medium shadow-[0_0_60px_-10px_rgba(157,76,221,0.6)] hover:scale-[1.04] active:scale-[0.98] transition-transform"
        aria-label="Open AI chat"
      >
        <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400 pulse-dot" />
        {open ? <X size={16} /> : <MessageSquare size={16} />}
        <span className="hidden sm:inline">{open ? "Close" : `Ask ${botName}`}</span>
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 18, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 18, scale: 0.97 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            data-testid="chat-widget-panel"
            className="fixed bottom-[9rem] right-6 sm:bottom-24 z-[55] w-[calc(100vw-3rem)] max-w-[380px] h-[540px] max-h-[70vh] sm:max-h-[80vh] glass-strong rounded-3xl flex flex-col overflow-hidden glow-purple"
          >
            <div className="flex items-center gap-3 p-4 border-b border-white/5">
              <div className="relative h-9 w-9 rounded-xl bg-gradient-to-br from-cyan-400 to-purple-500 flex items-center justify-center glow-cyan">
                <Sparkles size={16} className="text-black" />
              </div>
              <div className="flex-1">
                <div className="font-clash text-sm font-medium text-white flex items-center gap-2">
                  {botName}
                  <span className="inline-flex items-center gap-1 text-[10px] font-mono-acc tracking-widest text-emerald-400">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 pulse-dot" />
                    ONLINE
                  </span>
                </div>
                <div className="text-[11px] text-zinc-500">{brandShort} Growth Strategist</div>
              </div>
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="chat-messages">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                      m.role === "user" ? "bg-white text-black" : "bg-white/[0.05] text-zinc-100 border border-white/[0.06]"
                    }`}
                  >
                    {m.content}
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex justify-start">
                  <div className="bg-white/[0.05] border border-white/[0.06] rounded-2xl px-4 py-2.5 text-sm text-zinc-400 inline-flex items-center gap-1.5">
                    <span className="h-1.5 w-1.5 rounded-full bg-cyan-400 animate-pulse" />
                    <span className="h-1.5 w-1.5 rounded-full bg-cyan-400 animate-pulse [animation-delay:120ms]" />
                    <span className="h-1.5 w-1.5 rounded-full bg-cyan-400 animate-pulse [animation-delay:240ms]" />
                  </div>
                </div>
              )}
              {warming && !loading && messages.length === 1 && (
                <div className="flex justify-start" data-testid="chat-warming-notice">
                  <div className="text-[11px] text-zinc-500 italic px-1">
                    Waking up the AI… first message may take ~20s.
                  </div>
                </div>
              )}
            </div>

            <form onSubmit={send} className="border-t border-white/5 p-3 flex items-center gap-2">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask about scaling, ROAS, AI…"
                className="flex-1 bg-white/[0.04] border border-white/[0.06] rounded-full px-4 py-2.5 text-sm text-white placeholder:text-zinc-500 outline-none focus:border-purple-400/40 transition-colors"
                data-testid="chat-input"
              />
              <button
                type="submit"
                disabled={loading || !input.trim()}
                className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-cyan-400 to-purple-500 text-black disabled:opacity-40 hover:scale-105 active:scale-95 transition-transform"
                data-testid="chat-send"
                aria-label="Send"
              >
                <Send size={15} />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

// Contextual opening line based on what the visitor was just reading.
// Keeps things warm + specific without crossing into pushy.
function buildContextualPrompt(name, kind, botName) {
  const opener = `Hi — ${botName} here.`;
  if (kind === "service") {
    return `${opener} I noticed you were just reading our work on ${name}. Want me to map what a 90-day ${name} engagement would look like for your business — or shortlist the 2-3 quickest wins you could ship this month? Just tell me your industry + monthly revenue range and I'll get specific.`;
  }
  if (kind === "case-study") {
    return `${opener} You were reading the ${name} breakdown. Want me to estimate what a similar trajectory would look like for your business? Tell me your industry + current monthly revenue and I'll line up the closest playbook.`;
  }
  if (kind === "ai-solution") {
    return `${opener} You were looking at ${name}. Want me to map how we'd deploy it on your stack — and what the 30-day shipping plan would look like? Share your CRM + ad-platform + ballpark lead volume and I'll get specific.`;
  }
  if (kind === "process") {
    return `${opener} You were exploring step ${name} of our engagement model. Want me to walk through what that step would look like specifically for your business? Share a sentence about where you are and I'll get tactical.`;
  }
  if (kind === "insight") {
    return `${opener} Saw you reading "${name}". Want me to map how the playbook in that essay applies to your business? Share your industry + biggest current bottleneck and I'll go.`;
  }
  return `${opener} You were just reading about ${name}. Want me to map how we'd apply it specifically to your business?`;
}
